import React, { useState } from 'react';
import { Film, X, Sparkles, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Kontekstuelt promo-kort som gjør Reisefilm & Reisetavle oppdagbare på
 * hovedskjermen. Vises kun når det er relevant — dvs. når brukeren faktisk har
 * en reise å vise (≥2 innsjekk m/posisjon, beregnet i App og sendt som
 * `eligible`). Dismiss lagres per bruker i localStorage (samme idiom som
 * kart-hintet i Map.tsx), og coach-marken i Header venter til kortet er lukket.
 */

export const replayPromoDismissKey = (uid?: string | null) =>
  `fricamp_hint_replaypromo_v1_${uid || 'anon'}`;

/** Sant hvis brukeren allerede har lukket promo-kortet. */
export const isReplayPromoDismissed = (uid?: string | null): boolean => {
  try {
    return window.localStorage.getItem(replayPromoDismissKey(uid)) === '1';
  } catch {
    return false;
  }
};

interface ReplayPromoCardProps {
  /** hasReplayAccess(user) && ≥2 innsjekk m/posisjon — beregnet i App. */
  eligible: boolean;
  uid?: string | null;
  onOpenReplay: () => void;
  onOpenStickerBoard: () => void;
}

const ReplayPromoCard: React.FC<ReplayPromoCardProps> = ({ eligible, uid, onOpenReplay, onOpenStickerBoard }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => isReplayPromoDismissed(uid));

  if (!eligible || dismissed) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(replayPromoDismissKey(uid), '1');
      // La coach-marken i Header dukke opp i samme økt.
      window.dispatchEvent(new CustomEvent('fricamp:replaypromo-dismissed'));
    } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div className="relative rounded-2xl border border-brand/30 bg-gradient-to-br from-brand/15 via-[var(--bg-surface)] to-[var(--bg-surface-alt)] p-4 pr-9">
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('replayPromo.dismiss', 'Skjul')}
        className="absolute top-2.5 right-2.5 p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand/20 border border-brand/30 flex items-center justify-center flex-shrink-0">
          <Film size={20} className="text-brand" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-tiny font-black uppercase tracking-widest text-[var(--text-primary)]">
            {t('replayPromo.title', 'Se reisen din som film')}
          </h3>
          <p className="mt-1 text-2xs text-[var(--text-secondary)] leading-relaxed">
            {t('replayPromo.body', 'Du har besøkt flere plasser — lag en delbar reisefilm av turen din på sekunder.')}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={onOpenReplay}
              className="flex items-center gap-1.5 bg-brand hover:bg-brand-dark text-stone-900 px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-colors active:scale-95"
            >
              <Film size={13} />
              {t('replayPromo.cta', 'Lag reisefilm')}
            </button>
            <button
              type="button"
              onClick={onOpenStickerBoard}
              className="flex items-center gap-1 text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors px-2 py-2"
            >
              <Sparkles size={12} className="text-emerald-500" />
              {t('replayPromo.secondary', 'Se reisetavle')}
              <ChevronRight size={12} className="opacity-60" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplayPromoCard;